import { createGlobalStyle } from 'styled-components';

const GlobalStyles = createGlobalStyle`
  :root {
    /* 默认颜色变量 */
    --color-primary: #667eea;
    --color-secondary: #764ba2;
    --color-background: #ffffff;
    --color-surface: #f8fafc;
    --color-text-primary: #1a202c;
    --color-text-secondary: #4a5568;
    --color-text-muted: #718096;
    --color-border: #e2e8f0;
    --color-shadow: rgba(0, 0, 0, 0.1);
    --gradient-primary: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    
    /* 字体大小 */
    --font-size-base: 16px;
    
    /* 动画持续时间 */
    --animation-duration: 0.3s;
    
    /* 间距 */
    --spacing-xs: 4px;
    --spacing-sm: 8px;
    --spacing-md: 16px;
    --spacing-lg: 24px;
    --spacing-xl: 32px;
    --spacing-2xl: 48px;
    
    /* 圆角 */
    --border-radius-sm: 4px;
    --border-radius-md: 8px;
    --border-radius-lg: 12px;
    --border-radius-xl: 16px;
    --border-radius-full: 50%;
    
    /* 阴影 */
    --shadow-sm: 0 1px 2px var(--color-shadow);
    --shadow-md: 0 4px 6px var(--color-shadow);
    --shadow-lg: 0 10px 15px var(--color-shadow);
    --shadow-xl: 0 20px 25px var(--color-shadow);
    
    /* Z-index */
    --z-dropdown: 1000;
    --z-sticky: 1020;
    --z-fixed: 1030;
    --z-modal-backdrop: 1040;
    --z-modal: 1050;
    --z-popover: 1060;
    --z-tooltip: 1070;
    --z-toast: 1080;
  }
  
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  html {
    scroll-behavior: smooth;
    font-size: var(--font-size-base);
    overflow-x: hidden;
  }
  
  body {
    font-family: 'Noto Sans SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    line-height: 1.6;
    color: var(--color-text-primary);
    background-color: var(--color-background);
    transition: background-color var(--animation-duration) ease,
                color var(--animation-duration) ease;
    overflow-x: hidden;
  }
  
  /* 滚动条样式 */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  ::-webkit-scrollbar-track {
    background: var(--color-surface);
    border-radius: var(--border-radius-sm);
  }
  
  ::-webkit-scrollbar-thumb {
    background: var(--gradient-primary);
    border-radius: var(--border-radius-sm);
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  }
  
  /* 选择文本样式 */
  ::selection {
    background: rgba(102, 126, 234, 0.3);
    color: var(--color-text-primary);
  }
  
  ::-moz-selection {
    background: rgba(102, 126, 234, 0.3);
    color: var(--color-text-primary);
  }
  
  /* 焦点样式 */
  *:focus {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
  
  button:focus,
  input:focus,
  select:focus,
  textarea:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  /* 链接样式 */
  a {
    color: var(--color-primary);
    text-decoration: none;
    transition: color var(--animation-duration) ease;
  }
  
  a:hover {
    color: var(--color-secondary);
    text-decoration: underline;
  }
  
  /* 图片样式 */
  img {
    max-width: 100%;
    height: auto;
    display: block;
  }
  
  /* 按钮重置 */
  button {
    border: none;
    background: none;
    cursor: pointer;
    font-family: inherit;
    font-size: inherit;
  }
  
  /* 输入框重置 */
  input,
  textarea,
  select {
    font-family: inherit;
    font-size: inherit;
    border: none;
    outline: none;
  }
  
  /* 列表重置 */
  ul,
  ol {
    list-style: none;
  }
  
  /* 表格重置 */
  table {
    border-collapse: collapse;
    border-spacing: 0;
  }
  
  /* 标题样式 */
  h1, h2, h3, h4, h5, h6 {
    font-weight: 600;
    line-height: 1.2;
    color: var(--color-text-primary);
    margin-bottom: var(--spacing-md);
  }
  
  h1 {
    font-size: 2.5rem;
  }
  
  h2 {
    font-size: 2rem;
  }
  
  h3 {
    font-size: 1.75rem;
  }
  
  h4 {
    font-size: 1.5rem;
  }
  
  h5 {
    font-size: 1.25rem;
  }
  
  h6 {
    font-size: 1rem;
  }
  
  /* 段落样式 */
  p {
    margin-bottom: var(--spacing-md);
    color: var(--color-text-secondary);
  }
  
  /* 代码样式 */
  code {
    background: var(--color-surface);
    padding: 2px 6px;
    border-radius: var(--border-radius-sm);
    font-size: 0.875em;
    color: var(--color-primary);
    font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
  }
  
  pre {
    background: var(--color-surface);
    padding: var(--spacing-lg);
    border-radius: var(--border-radius-md);
    overflow-x: auto;
    margin: var(--spacing-lg) 0;
    border: 1px solid var(--color-border);
  }
  
  pre code {
    background: none;
    padding: 0;
    color: inherit;
  }
  
  /* 引用样式 */
  blockquote {
    border-left: 4px solid var(--color-primary);
    padding-left: var(--spacing-lg);
    margin: var(--spacing-lg) 0;
    font-style: italic;
    color: var(--color-text-muted);
  }
  
  /* 分割线 */
  hr {
    border: none;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--color-border), transparent);
    margin: var(--spacing-2xl) 0;
  }
  
  /* 工具类 */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  
  .hidden {
    display: none !important;
  }
  
  .invisible {
    visibility: hidden;
  }
  
  .disabled {
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
  }
  
  /* 动画类 */
  .fade-in {
    animation: fadeIn var(--animation-duration) ease-in-out;
  }
  
  .fade-out {
    animation: fadeOut var(--animation-duration) ease-in-out;
  }
  
  .slide-in-up {
    animation: slideInUp var(--animation-duration) ease-out;
  }
  
  .slide-in-down {
    animation: slideInDown var(--animation-duration) ease-out;
  }
  
  .scale-in {
    animation: scaleIn var(--animation-duration) ease-out;
  }
  
  /* 动画定义 */
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  @keyframes fadeOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
  
  @keyframes slideInUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  @keyframes slideInDown {
    from {
      transform: translateY(-20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  @keyframes scaleIn {
    from {
      transform: scale(0.9);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  
  @keyframes bounce {
    0%, 20%, 53%, 80%, 100% {
      transform: translate3d(0, 0, 0);
    }
    40%, 43% {
      transform: translate3d(0, -30px, 0);
    }
    70% {
      transform: translate3d(0, -15px, 0);
    }
    90% {
      transform: translate3d(0, -4px, 0);
    }
  }
  
  /* 响应式断点 */
  @media (max-width: 640px) {
    :root {
      --font-size-base: 14px;
    }
    
    h1 {
      font-size: 2rem;
    }
    
    h2 {
      font-size: 1.75rem;
    }
    
    h3 {
      font-size: 1.5rem;
    }
    
    h4 {
      font-size: 1.25rem;
    }
  }
  
  @media (max-width: 480px) {
    :root {
      --spacing-md: 12px;
      --spacing-lg: 20px;
      --spacing-xl: 28px;
      --spacing-2xl: 40px;
    }
  }
  
  /* 打印样式 */
  @media print {
    * {
      background: transparent !important;
      color: black !important;
      box-shadow: none !important;
      text-shadow: none !important;
    }
    
    a,
    a:visited {
      text-decoration: underline;
    }
    
    a[href]:after {
      content: " (" attr(href) ")";
    }
    
    abbr[title]:after {
      content: " (" attr(title) ")";
    }
    
    .no-print {
      display: none !important;
    }
  }
  
  /* 高对比度模式 */
  @media (prefers-contrast: high) {
    :root {
      --color-border: #000000;
      --color-shadow: rgba(0, 0, 0, 0.5);
    }
  }
  
  /* 减少动画模式 */
  @media (prefers-reduced-motion: reduce) {
    :root {
      --animation-duration: 0s;
    }
    
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
`;

export default GlobalStyles;
